import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { researchAPI } from '@/lib/api';
import { toast } from 'sonner';
import { BrutalCard, BrutalButton } from '@/components/brutal';
import { Database, Download } from 'lucide-react';

const Stat = ({ label, value }) => (
  <div className="border-2 border-black p-3 bg-white"><p className="text-xs font-bold uppercase text-gray-600">{label}</p><p className="text-2xl font-black">{value}</p></div>
);

// Consented, pseudonymous, aggregate-only research data. Small groups are never shown.
const ResearchTab = () => {
  const qc = useQueryClient();
  const { data: sum } = useQuery({ queryKey: ['research-summary'], queryFn: async () => (await researchAPI.summary()).data });
  const { data: agg } = useQuery({ queryKey: ['research-aggregates'], queryFn: async () => (await researchAPI.aggregates()).data });
  const { data: dict } = useQuery({ queryKey: ['research-dictionary'], queryFn: async () => (await researchAPI.dictionary()).data });
  const [busy, setBusy] = useState(false);

  const rebuild = async () => {
    setBusy(true);
    try {
      await researchAPI.rebuild();
      await qc.invalidateQueries({ queryKey: ['research-summary'] });
      await qc.invalidateQueries({ queryKey: ['research-aggregates'] });
      toast.success('Research data rebuilt');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Rebuild failed'); }
    setBusy(false);
  };

  const download = async () => {
    try {
      const { data } = await researchAPI.csv();
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url; a.download = 'research-aggregates.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Could not download'); }
  };

  const last = sum?.last_build;
  return (
    <div className="space-y-6" data-testid="research-tab">
      <BrutalCard className="p-5">
        <h3 className="text-xl font-black uppercase mb-3 flex items-center gap-2"><Database size={20} /> Research data</h3>
        <p className="text-sm mb-4">Only children whose parent opted in are included, under a code name, with numbers only (never names, writing, audio, beliefs or culture). Totals appear only for groups of at least {sum?.min_group_size || 20} children.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <Stat label="Opted in" value={`${sum?.consented_students ?? '…'} of ${sum?.total_students ?? '…'}`} />
          <Stat label="Totals published" value={sum?.published_cells ?? '…'} />
          <Stat label="Last build" value={last ? new Date(last.built_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'never'} />
          <Stat label="Key set" value={sum?.configured ? 'yes' : 'NO'} />
        </div>
        {sum && !sum.configured && <p className="text-sm font-bold text-rose-700 mb-3" data-testid="research-not-configured">Set RESEARCH_PSEUDONYM_KEY (32+ random characters) in Render before building.</p>}
        <div className="flex gap-2 flex-wrap">
          <BrutalButton variant="amber" onClick={rebuild} disabled={busy || !sum?.configured} data-testid="research-rebuild">{busy ? 'Building…' : 'Rebuild now'}</BrutalButton>
          <BrutalButton variant="default" onClick={download} disabled={!agg?.cells?.length}><Download size={14} className="inline mr-1" /> Download totals (CSV)</BrutalButton>
        </div>
        {last && (
          <div className="mt-4 text-sm">
            <p className="font-bold">Last build: {last.facts} rows from {last.consented_students} children</p>
            {Object.entries(last.metrics || {}).map(([m, r]) => (
              <p key={m} className="text-gray-700">{m}: {r.cells_published} published, {r.cells_suppressed} held back (too few children)</p>
            ))}
          </div>
        )}
      </BrutalCard>

      <BrutalCard className="p-5">
        <h3 className="text-lg font-black uppercase mb-3">Published totals</h3>
        {!agg?.cells?.length ? <p className="text-sm text-gray-600">Nothing yet. Totals appear once enough opted-in children share the same age band, word or step.</p> : (
          <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="text-left border-b-2 border-black"><th className="py-2 pr-3">Metric</th><th className="pr-3">Group</th><th className="pr-3">Children</th><th>Values</th></tr></thead>
            <tbody>{agg.cells.map((c, i) => (
              <tr key={i} className="border-b border-gray-300 align-top"><td className="py-2 pr-3 font-bold">{c.metric}</td><td className="pr-3">{Object.entries(c.dims).map(([k, v]) => `${k}: ${v}`).join(', ')}</td><td className="pr-3">{c.n_students}</td><td className="font-mono text-xs">{JSON.stringify(c.values)}</td></tr>
            ))}</tbody></table></div>
        )}
      </BrutalCard>

      <BrutalCard className="p-5">
        <h3 className="text-lg font-black uppercase mb-3">What is and is not collected</h3>
        <pre className="text-xs whitespace-pre-wrap bg-gray-50 border-2 border-black p-3 max-h-96 overflow-auto" data-testid="research-dictionary">{dict?.markdown || 'Loading…'}</pre>
      </BrutalCard>
    </div>
  );
};

export default ResearchTab;
